import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUser } from '../../services/api/UserService';
import AdminHeader from '../../components/headers/AdminHeader';
import iconTitle from '../../assets/img/icon-square.svg';
import iconAccount from '../../assets/img/as-finder-logo-icon.svg';
import { validateAccountEditForm, getFirstErrorMessage } from '../../utils/accountEditValidation';
import { showSuccessDialog } from '../../providers/ErrorDialogProvider';
import '../../styles/AccountCreate.css';
import '../../styles/button.css';
import '../../styles/PasswordValidation.css';

const AccountCreate = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    accountname: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // フィールドバリデーション状態
  const [fieldErrors, setFieldErrors] = useState({
    username: [],     // ユーザー名のエラーメッセージ配列
    accountname: []   // アカウント名のエラーメッセージ配列
  });
  const [showFieldErrors, setShowFieldErrors] = useState({
    username: false,    // ユーザー名エラーの表示フラグ
    accountname: false  // アカウント名エラーの表示フラグ
  });
  const [touchedFields, setTouchedFields] = useState({
    username: false,    // ユーザー名フィールドがタッチされたか
    accountname: false  // アカウント名フィールドがタッチされたか
  });

  // 指定されたフィールドのバリデーションを実行する関数
  const validateField = (fieldName, value) => {
    // formDataの構造をAccountEditFormに合わせる（name -> username）
    const tempFormData = { 
      ...formData, 
      username: fieldName === 'name' ? value : formData.name,
      accountname: fieldName === 'accountname' ? value : formData.accountname
    };
    const validationResult = validateAccountEditForm(tempFormData);
    
    // fieldNameを正規化（name -> username）
    const normalizedFieldName = fieldName === 'name' ? 'username' : fieldName;
    
    // エラー状態を更新
    setFieldErrors(prev => ({
      ...prev,
      [normalizedFieldName]: validationResult[normalizedFieldName]
    }));
    
    // バリデーション結果を返す（true: 成功, false: 失敗）
    return validationResult[normalizedFieldName].length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // formDataを更新
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // リアルタイムバリデーション実行
    if (name === 'name' || name === 'accountname') {
      validateField(name, value);
      
      // fieldNameを正規化
      const normalizedFieldName = name === 'name' ? 'username' : name;
      
      // フィールドがタッチされている場合のみエラーを表示
      if (touchedFields[normalizedFieldName]) {
        setShowFieldErrors(prev => ({
          ...prev,
          [normalizedFieldName]: true
        }));
      }
    }
    
    // 入力時にフォーム全体のエラーメッセージをクリア
    if (error) {
      setError('');
    }
  };

  // フィールドからフォーカスが外れた時の処理
  const handleFieldBlur = (e) => {
    const { name } = e.target;
    
    // fieldNameを正規化
    const normalizedFieldName = name === 'name' ? 'username' : name;
    
    if (name === 'name' || name === 'accountname') {
      // フィールドがタッチされた状態にする
      setTouchedFields(prev => ({
        ...prev,
        [normalizedFieldName]: true
      }));
      
      // エラー表示を有効にする
      setShowFieldErrors(prev => ({
        ...prev,
        [normalizedFieldName]: true
      }));
    }
  };

  const handleFieldFocus = (e) => {
    const { name } = e.target;
    
    // fieldNameを正規化
    const normalizedFieldName = name === 'name' ? 'username' : name;
    
    if (name === 'name' || name === 'accountname') {
      // フォーカス時はエラー表示を一時的に隠す
      setShowFieldErrors(prev => ({
        ...prev,
        [normalizedFieldName]: false
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // バリデーション用のformDataを準備（name -> username）
    const validationFormData = {
      username: formData.name,
      accountname: formData.accountname
    };

    // 基本フィールド（ユーザー名、アカウント名）のバリデーション実行
    const validationResult = validateAccountEditForm(validationFormData);
    
    // フィールドバリデーションでエラーがある場合
    if (validationResult.hasErrors) {
      // エラー状態を全て更新
      setFieldErrors({
        username: validationResult.username,
        accountname: validationResult.accountname
      });
      
      // エラー表示を有効化
      setShowFieldErrors({
        username: true,
        accountname: true
      });
      
      // タッチ状態を有効化
      setTouchedFields({
        username: true,
        accountname: true
      });
      
      // 最初のエラーメッセージを表示
      const firstError = getFirstErrorMessage(validationResult);
      setError(firstError);
      return;
    }

    setLoading(true);
    
    try {
      // APIリクエストの準備
      const userData = {
        username: formData.name,
        accountname: formData.accountname,
        is_admin: true,
        is_agent: true,
        is_solicitor: true
      };
      
      // APIエンドポイントにリクエストを送信
      const response = await createUser(userData);

      setLoading(false);

      // 成功メッセージを表示
      showSuccessDialog('アカウント作成に成功しました。');

      console.log('アカウント作成成功:', response);
      
      // 詳細画面に遷移
      navigate(`/admin/account/list`);
    } catch (error) {
      // エラーダイアログ表示はBaseApiServiceで行う
      // エラーコンソール出力は各ApiServiceで行う
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="account-create-page">
      <AdminHeader onMenuToggle={(isOpen) => {
        // メニューの状態が変更された時の処理をここに追加できます
      }} />
      <div className="page-container">
        <div className="content-wrapper">
          <div className="page-header">
            <div className="title-section">
              <div className="icon-square">
                <img src={iconTitle} alt="タイトルアイコン" />
              </div>
              <h1>基本情報</h1>
            </div>
          </div>
          <div className="l-layout__account max-middle l-layout__account-account">
            <figure className="l-layout__account-left">
              <img src={iconAccount} alt="タイトルアイコン" />
            </figure>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <form className="account-form l-layout__account-right" onSubmit={handleSubmit}>
              {/* ユーザー名フィールド */}
              <div className="form-group form-grid">
                <label htmlFor="name" className="form-label">ユーザー名</label>
                <div className="form-input-container">
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className={`p-account-box ${showFieldErrors.username && fieldErrors.username.length > 0 ? 'error' : ''} ${showFieldErrors.username && fieldErrors.username.length === 0 && touchedFields.username ? 'success' : ''}`}
                    value={formData.name}
                    onChange={handleInputChange}
                    onBlur={handleFieldBlur}
                    onFocus={handleFieldFocus}
                    disabled={loading}
                  />
                  {/* ユーザー名バリデーションエラー表示 */}
                  {showFieldErrors.username && fieldErrors.username.length > 0 && (
                    <div className="password-validation-errors">
                      {fieldErrors.username.map((error, index) => (
                        <div key={index} className="validation-error">
                          {error}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* アカウント名（メールアドレス）フィールド */}
              <div className="form-group form-grid">
                <label htmlFor="accountname" className="form-label">アカウント名（メールアドレス）</label>
                <div className="form-input-container">
                  <input
                    type="email"
                    id="accountname"
                    name="accountname"
                    className={`p-account-box ${showFieldErrors.accountname && fieldErrors.accountname.length > 0 ? 'error' : ''} ${showFieldErrors.accountname && fieldErrors.accountname.length === 0 && touchedFields.accountname ? 'success' : ''}`}
                    value={formData.accountname}
                    onChange={handleInputChange}
                    onBlur={handleFieldBlur}
                    onFocus={handleFieldFocus}
                    disabled={loading}
                  />
                  {/* アカウント名（メールアドレス）バリデーションエラー表示 */}
                  {showFieldErrors.accountname && fieldErrors.accountname.length > 0 && (
                    <div className="password-validation-errors">
                      {fieldErrors.accountname.map((error, index) => (
                        <div key={index} className="validation-error">
                          {error}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="button-group button-group--mt-l">
                <button 
                  type="button" 
                  className="button button-secondary button-large" 
                  onClick={() => navigate(-1)}
                  disabled={loading}
                >
                  戻る
                </button>
                <button 
                  type="submit" 
                  className="button button-primary button-large"
                  disabled={loading}
                >
                  {loading ? 'アカウント作成中...' : '新規作成'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountCreate;